import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSearch } from "@tanstack/react-router";
import { Button, Image, Space, Table, Tag } from "antd";
import { AddDrawer } from "components/Drawers/FormDesignAdd/Preview/AddDrawer";
import { EditDrawer } from "components/Drawers/FormDesignAdd/Preview/EditDrawer";
import { DataType, initialData } from "components/Tables/FormDesignTable/temp";
import { DragHandle } from "components/Tables/FormDesignTable/UI/DnD/DragHandle";
import { Row } from "components/Tables/FormDesignTable/UI/DnD/Row";
import { DrawerLink } from "components/UI/DrawerLink";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useState } from "react";
import { DrawerId } from "types/models";

import { FormDesignPreviewFormData, schema } from "./formSchema";

import FormData = App.Data.FormData;

import type { DragEndEvent } from "@dnd-kit/core";

interface FormDesignPreviewFormProps {
    formData: FormData;
}

interface SearchParams {
    [`formDesignPreviewEdit-formId`]: string;
}

interface FormContentProps {
    dataSource: DataType[];
    onDragEnd: (event: DragEndEvent) => void;
    handleSave: (newData: DataType) => void;
    handleUpdate: (newData: DataType) => void;
}

const FormContent: FC<FormContentProps> = ({ dataSource, onDragEnd, handleSave, handleUpdate }) => {
    const { t } = usePaldockTranslation("forms.form-design-preview");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const columns = [
        {
            key: "sort",
            width: 50,
            render: () => <DragHandle />,
        },
        {
            title: t("fields.logo"),
            dataIndex: "logo",
            key: "logo",
            render: (logo: string) => logo && <Image src={logo} alt="Logo" width={50} height={25} />,
        },
        {
            title: t("fields.company"),
            dataIndex: "company",
            key: "company",
        },
        {
            title: t("fields.fee"),
            dataIndex: "fee",
            key: "fee",
        },
        {
            title: t("fields.approval_chance"),
            dataIndex: "approvalChance",
            key: "approvalChance",
        },
        {
            title: t("fields.status"),
            dataIndex: "status",
            key: "status",
            render: (status: string, record: DataType) => (
                <Tag color={record.statusType === "active" ? "green" : "volcano"}>{status}</Tag>
            ),
        },
        {
            title: tG("actions"),
            key: "action",
            render: (record: DataType) => (
                <Space size="middle">
                    <DrawerLink
                        drawerId={DrawerId.FormDesignPreviewEdit}
                        action={"show"}
                        search={{ [`${DrawerId.FormDesignPreviewEdit}-formId`]: record.key }}
                    >
                        <Button icon={<SettingOutlined />} />
                    </DrawerLink>
                    <Button icon={<DeleteOutlined />} />
                </Space>
            ),
        },
    ];

    return (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext items={dataSource.map((i) => i.key)} strategy={verticalListSortingStrategy}>
                <Table rowKey="key" components={{ body: { row: Row } }} columns={columns} dataSource={dataSource} />
            </SortableContext>
            <DrawerLink drawerId={DrawerId.FormDesignPreviewAdd} action={"show"}>
                <Button type="primary" className="w-full">
                    {tC("add")}
                </Button>
            </DrawerLink>
            <AddDrawer onSubmit={handleSave} />
            {searchParams[`${DrawerId.FormDesignPreviewEdit}-formId`] && (
                <EditDrawer
                    formId={searchParams?.[`${DrawerId.FormDesignPreviewEdit}-formId`]}
                    onUpdate={handleUpdate}
                    // TODO: odstranit pak
                    dataSource={dataSource}
                />
            )}
        </DndContext>
    );
};

export const FormDesignPreviewForm: FC<FormDesignPreviewFormProps> = ({ formData }) => {
    console.log(formData);

    const [dataSource, setDataSource] = useState<DataType[]>(initialData);

    const handleSave = (newData: DataType) => {
        const newDataSource = [...dataSource];
        newDataSource.push(newData);
        setDataSource(newDataSource);
    };

    const handleUpdate = (newData: DataType) => {
        const newDataSource = [...dataSource];
        const index = newDataSource.findIndex((item) => newData.key === item.key);
        if (index > -1) {
            const item = newDataSource[index];
            newDataSource.splice(index, 1, { ...item, ...newData });
            setDataSource(newDataSource);
        }
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setDataSource((prevState) => {
                const activeIndex = prevState.findIndex((record) => record.key === active?.id);
                const overIndex = prevState.findIndex((record) => record.key === over?.id);
                return arrayMove(prevState, activeIndex, overIndex);
            });
        }
    };

    return (
        <GeneralFormProvider<FormDesignPreviewFormData> schema={schema} defaultValues={dataSource[0]}>
            <FormContent
                dataSource={dataSource}
                onDragEnd={onDragEnd}
                handleSave={handleSave}
                handleUpdate={handleUpdate}
            />
        </GeneralFormProvider>
    );
};
